import { getReservableShowings } from '@dwellet/dwellet-utils'
import moment, { Moment } from 'moment-timezone'
import { stringOrDate } from 'react-big-calendar'
import i18n from '../translations/i18n'
import {
  CreateShowing,
  Showing,
  ShowingType,
  checkIfReservedShowing,
  ReservedShowing,
  FormShowing
} from '../types/showings'
import {
  ShowingsInterface,
  ApartmentIdIndex,
  IndexedShowingsInterfaces
} from '../types/states'
import { getDifferenceInMinutes } from './dates'
import { compareStartDates } from './sorting'

const typeTranslations = {
  openHouse: 'apartment.scheduler.type.openHouse',
  personal: 'apartment.scheduler.type.personal',
  reserved: 'showings.reservations.label'
}

const timeslotTranslations = {
  reserved: 'apartment.scheduler.showings.reserved',
  free: 'apartment.scheduler.showings.free'
}

const getStatusMessage = (status: string, type: string) =>
  i18n.t(timeslotTranslations[status], {
    type: i18n.t(typeTranslations[type])
  })

const createDateTitle = (
  startDate: stringOrDate | undefined,
  endDate: stringOrDate
) => {
  return `${i18n.t('apartment.scheduler.showing')} + ${startDate} - ${endDate}`
}

const createTimeslotTitle = (showing: Showing, type: ShowingType) => {
  // const start = dateFormat(path(['startDate'], showing), 'dd.mm.yyyy HH:MM')
  // const end = dateFormat(path(['endDate'], showing), 'HH:MM')
  const status = showing.applicationIds ? 'reserved' : 'free'
  const statusMessage = getStatusMessage(status, type)
  const message = `${statusMessage}`
  return message
}

const generateCreateShowings = (
  startDate: string,
  endDate: string,
  type: ShowingType
) => {
  let start = new Date(startDate)
  let createShowings: any[] = []
  const duration = 0 // always 30 min
  // const timeslotsToCreate = getAmountOfTimeslots(startDate, endDate, duration)
  // if (timeslotsToCreate > 0) {
  //   for (let i = 0; i < timeslotsToCreate; i++) {
  const newStart = start.toISOString()
  start = new Date(start.getTime() + duration * 60 * 1000)
  createShowings = [
    ...createShowings,
    {
      startDate: newStart,
      endDate: start.toISOString(),
      duration,
      type
    }
  ]
  //   }
  // }

  return createShowings
}

const getShowingsInterface = (showings: Showing[]): ShowingsInterface => {
  const showingsType =
    showings && showings.length > 0 ? showings[0].type : undefined
  return {
    showings: showings.sort(compareStartDates),
    // TODO: remove any, currently getReservableShowings is described in @dwellet/dwellet-utils
    reservableShowings: showingsType
      ? (getReservableShowings(showings as any, showingsType) as any)
      : { reservables: [], bestReservables: [] },
    reservations: showings.filter((showing: Showing) =>
      checkIfReservedShowing(showing)
    ) as ReservedShowing[]
  }
}

const getShowingsInterfacesIndexedByApartmentId = (
  showings: Showing[]
): IndexedShowingsInterfaces => {
  let showingsInterfaceByApartmentId:
    | ApartmentIdIndex<ShowingsInterface>
    | ApartmentIdIndex<never> = {}
  const showingsIndexedByApartmentId: ApartmentIdIndex<
    Showing[]
  > = showings.reduce(
    (showingsAcc: ApartmentIdIndex<Showing[]>, showing: Showing) => {
      if (showingsAcc[showing.apartmentId]) {
        showingsAcc[showing.apartmentId].push(showing)
      } else {
        showingsAcc[showing.apartmentId] = [showing]
      }
      return showingsAcc
    },
    {}
  )

  Object.keys(showingsIndexedByApartmentId || {}).forEach((apartmentId) => {
    showingsInterfaceByApartmentId = {
      ...showingsInterfaceByApartmentId,
      [apartmentId]: getShowingsInterface(
        showingsIndexedByApartmentId[apartmentId]
      )
    }
  })

  return showingsInterfaceByApartmentId
}

const getDifferenceBetweenStartAndEndInMin = (
  showings: Showing[] | CreateShowing[] | FormShowing[]
) => {
  const firstStart = showings[0].startDate
  const lastEnd = showings[showings.length - 1].endDate
  return getDifferenceInMinutes(lastEnd, firstStart)
}

const getShowingIdFromDateTime = (
  showings: Showing[],
  date: Moment,
  time: Moment
) => {
  return showings.find(
    (showing) =>
      moment(showing.startDate).format('YYYY/MM/DD') ===
        moment(date).format('YYYY/MM/DD') &&
      moment(showing.startDate).format('H:mm') === moment(time).format('H:mm')
  )?.showingId
}

const getShowingsTimesFromSpesificDate = (
  showings: (Showing | CreateShowing | FormShowing)[],
  date: string | Moment
): Moment[] => {
  const array: Moment[] = []
  showings?.forEach((e) => {
    moment(e.startDate).format('DD/MM/YYYY') ===
      moment(date).format('DD/MM/YYYY') && array.push(moment(e.startDate))
  })
  return array
}

const getShowingsOnSpesificDate = (
  showings: (Showing | CreateShowing | FormShowing)[],
  date: string | Moment
): (Showing | CreateShowing | FormShowing)[] => {
  if (showings && showings.length != 0) {
    return showings.filter((showing) => {
      const compareDate = moment(date)
      const startDate = moment(showing.startDate)
      return compareDate.format('DD:MM') === startDate.format('DD:MM')
    })
  } else return []
}

export {
  createDateTitle,
  createTimeslotTitle,
  getStatusMessage,
  generateCreateShowings,
  getShowingsInterface,
  getShowingsInterfacesIndexedByApartmentId,
  getDifferenceBetweenStartAndEndInMin,
  getShowingIdFromDateTime,
  getShowingsTimesFromSpesificDate,
  getShowingsOnSpesificDate
}
